import { DteEmitido } from "src/app/models/Dte/DteEmitido";
import { DetalleDte } from "src/app/models/Dte/DetalleDte";
import { DetalleDteImpuestoAdicional } from "src/app/models/Dte/DetalleDteImpuestoAdicional";
import { OptionsReferenceDteEmitido } from "src/app/models/Dte/OptionsReferenceDteEmitido";
import { OptionsTransportDteEmitido } from "src/app/models/Dte/OptionsTransportDteEmitido";
import { ReferenciaDte } from "src/app/models/Dte/ReferenciaDte";
import { ReferenciaDteEmitido } from "src/app/models/Dte/ReferenciaDteEmitido";
import { Impuesto } from "src/app/models/Impuesto";
import { TipoDte } from "src/app/models/tipoDte";

type SelectItem = { id: number | null; [key: string]: any };

export interface ResumenPatchSources {
	tipoDteSource?: TipoDte[];
	formasPagoSource?: SelectItem[];
	medioPagoSource?: SelectItem[];
	formasPagoExportacionSource?: SelectItem[];
	tipoTrasladosSource?: SelectItem[];
	tipoDespachoSource?: SelectItem[];
	sizeFormat?: Array<{ id: number; name: string; size: number }>;
}

export interface ResumenPatchForCopiedDte {
	tipoDte?: TipoDte;
	formaPago?: SelectItem;
	medioPagoBoleta?: SelectItem;
	formaPagoExportacion?: SelectItem;
	tipoTraslado?: SelectItem;
	tipoDespacho?: SelectItem;
	fechaEmision?: Date;
	diasVencimiento: number;
	descuentoGlobal: number;
	formato?: { id: number; name: string; size: number };
	tieneIvaTerceros: boolean;
	ivaTerceros: number;
	ivaPropio: number;
	aplicaCredEC: boolean;
	observaciones: string;
}

export function mapReferenciasDetalleToEmitidas(referencias?: ReferenciaDte[], fallback?: ReferenciaDteEmitido[]): ReferenciaDteEmitido[] {
	if (referencias && referencias.length > 0) {
		return referencias.map((referencia) => {
			const referenciaActual = referencia as ReferenciaDte & { tipo?: string; folio?: string };
			const ref = Object.assign(new ReferenciaDteEmitido(), {
				codigoTipoDteReferencia: referenciaActual.tipo ?? (referencia.dte?.codigoTipoDte != null ? referencia.dte.codigoTipoDte.toString() : ""),
				folioReferencia: referenciaActual.folio ?? (referencia.dte?.folio != null ? referencia.dte.folio.toString() : ""),
				fechaDteReferenciado: referencia.dte?.fecha ?? referencia.fecha,
				razonReferencia: referenciaActual.razonRef ?? referencia.glosa ?? "",
			});

			return ref;
		});
	}

	return fallback ? fallback.map((referencia) => Object.assign(new ReferenciaDteEmitido(), referencia)) : [];
}

export function mapDetallesForCopiedDte(detalles?: DetalleDte[], impuestosSource: Impuesto[] = []): DetalleDte[] {
	return (detalles ?? []).map((detalle) => {
		const mapped = Object.assign(new DetalleDte(), detalle);
		const cantidad = Number(mapped.cantidad ?? 0);
		const precioUnitario = Number(mapped.precioUnitario ?? 0);
		const descuento = Number(mapped.descuento ?? 0);
		const recargo = Number(mapped.recargo ?? 0);
		const total = Number(mapped.total ?? 0);
		const subtotalCalculado = cantidad * precioUnitario;
		const subtotal = mapped.subtotal != null && Number(mapped.subtotal) > 0 ? Number(mapped.subtotal) : subtotalCalculado > 0 ? subtotalCalculado : total + descuento - recargo;

		mapped.cantidad = cantidad;
		mapped.precioUnitario = precioUnitario;
		mapped.descuento = descuento > 0 ? descuento : undefined;
		mapped.recargo = recargo > 0 ? recargo : undefined;
		mapped.subtotal = subtotal;
		mapped.total = total > 0 ? total : subtotal + recargo - descuento;
		mapped.rebajaAvaluo = Number(mapped.rebajaAvaluo ?? 0);
		mapped.impuestosAdicionales = (mapped.impuestosAdicionales ?? []).map((impuesto) => {
			const impuestoAdicional = Object.assign(new DetalleDteImpuestoAdicional(), impuesto);
			impuestoAdicional.codigoSii = Number(impuestoAdicional.codigoSii ?? 0);
			impuestoAdicional.valor = Number(impuestoAdicional.valor ?? 0);
			const tasa = Number(impuestoAdicional.tasa ?? 0);
			impuestoAdicional.tasa = tasa > 0 ? tasa : getTasaImpuestoAdicional(impuestoAdicional.codigoSii, impuestosSource);
			return impuestoAdicional;
		});
		mapped.totalImpuestosAdicionales = Number(mapped.totalImpuestosAdicionales ?? 0);
		if (mapped.totalImpuestosAdicionales <= 0 && mapped.impuestosAdicionales.length > 0) {
			mapped.totalImpuestosAdicionales = mapped.impuestosAdicionales.reduce((totalImpuestos, impuesto) => totalImpuestos + Number(impuesto.valor ?? 0), 0);
		}

		return mapped;
	});
}

function getTasaImpuestoAdicional(codigoSii: number, impuestosSource: Impuesto[]): number {
	const impuesto = impuestosSource.find((item) => Number(item.tipoImpuesto) === codigoSii);
	return Number(impuesto?.valor ?? 0);
}

export function buildResumenPatchForCopiedDte(dte: Partial<DteEmitido> & { [key: string]: any }, sources: ResumenPatchSources): ResumenPatchForCopiedDte {
	const ivaTerceros = Number(dte.ivaTerceros ?? dte.IvaTerceros ?? 0);
	const ivaPropio = Number(dte.ivaPropio ?? dte.IvaPropio ?? 0);
	const observaciones = dte.observaciones ?? dte.Observaciones ?? "";
	const guiaDespacho = dte.guiaDespacho ?? dte.GuiaDespacho ?? {};

	return {
		tipoDte: getTipoDteFromCopiedDte(dte, sources.tipoDteSource ?? []),
		formaPago: getSelectItemFromCopiedDte(dte.formaPagoEnum ?? dte.FormaPagoEnum, dte.formaPago ?? dte.FormaPago, sources.formasPagoSource ?? [], "nombreForma"),
		medioPagoBoleta: getSelectItemFromCopiedDte(dte.medioPagoBoletaEnum ?? dte.MedioPagoBoletaEnum, dte.medioPagoBoleta ?? dte.medioPago ?? dte.MedioPagoBoleta, sources.medioPagoSource ?? [], "nombreMedio"),
		formaPagoExportacion: getSelectItemFromCopiedDte(dte.FormaPagoExportacionEnum ?? dte.formaPagoExportacionEnum, dte.FormaPagoExportacion ?? dte.formaPagoExportacion, sources.formasPagoExportacionSource ?? [], "descripcion"),
		tipoTraslado: getSelectItemFromCopiedDte(guiaDespacho.tipoTraslado ?? guiaDespacho.TipoTraslado, guiaDespacho.nombreTipoTraslado ?? guiaDespacho.NombreTipoTraslado, sources.tipoTrasladosSource ?? [], "nombre"),
		tipoDespacho: getSelectItemFromCopiedDte(guiaDespacho.tipoDespacho ?? guiaDespacho.TipoDespacho, guiaDespacho.nombreTipoDespacho ?? guiaDespacho.NombreTipoDespacho, sources.tipoDespachoSource ?? [], "nombre"),
		fechaEmision: getDateOrUndefined(dte.fecha ?? dte.Fecha),
		diasVencimiento: getDiasVencimientoFromCopiedDte(dte),
		descuentoGlobal: Number(dte.descuentoGlobal ?? dte.DescuentoGlobal ?? 0),
		formato: getFormatoFromCopiedDte(dte.formato ?? dte.Formato, sources.sizeFormat ?? []),
		tieneIvaTerceros: Boolean(dte.tieneIvaTerceros ?? dte.TieneIvaTerceros ?? (ivaTerceros > 0 || ivaPropio > 0)),
		ivaTerceros,
		ivaPropio,
		aplicaCredEC: Boolean(dte.aplicaCredEC ?? dte.AplicaCredEC ?? Number(dte.credEC ?? dte.CredEC ?? 0) > 0),
		observaciones,
	};
}

export function buildTransportOptionsForCopiedDte(dte: Partial<DteEmitido> & { [key: string]: any }): OptionsTransportDteEmitido {
	const guiaDespacho = dte.guiaDespacho ?? dte.GuiaDespacho ?? {};

	return Object.assign(new OptionsTransportDteEmitido(), {
		rutTransporte: guiaDespacho.rutTransporte ?? guiaDespacho.RutTransporte,
		patente: guiaDespacho.patente ?? guiaDespacho.Patente,
		rutChofer: guiaDespacho.rutChofer ?? guiaDespacho.RutChofer,
		nombreChofer: guiaDespacho.nombreChofer ?? guiaDespacho.NombreChofer,
		dvChofer: guiaDespacho.dvChofer ?? guiaDespacho.DvChofer,
		dvTransporte: guiaDespacho.dvTransporte ?? guiaDespacho.DvTransporte,
		comunaOrigenId: dte.comunaOrigenId ?? dte.ComunaOrigenId,
		mnzRolOrigen: dte.mnzRolOrigen ?? dte.MnzRolOrigen,
		prdRolOrigen: dte.prdRolOrigen ?? dte.PrdRolOrigen,
		codPlanConaf: dte.codPlanConaf ?? dte.CodPlanConaf,
		latitudOrigenMadera: dte.latitudOrigenMadera ?? dte.LatitudOrigenMadera,
		longitudOrigenMadera: dte.longitudOrigenMadera ?? dte.LongitudOrigenMadera,
	});
}

function getTipoDteFromCopiedDte(dte: Partial<DteEmitido>, tipoDteSource: TipoDte[]): TipoDte | undefined {
	return tipoDteSource.find((tipo) => tipo.tipoDteId === dte.tipoDteId) ?? tipoDteSource.find((tipo) => Number(tipo.codigosii) === Number(dte.codigoTipoDte));
}

function getSelectItemFromCopiedDte(idValue: unknown, textValue: unknown, source: SelectItem[], labelKey: string): SelectItem | undefined {
	const id = Number(idValue);
	if (!isNaN(id) && id > 0) {
		const itemById = source.find((item) => Number(item.id) === id);
		if (itemById) return itemById;
	}

	const text = normalizeText(textValue);
	return text ? source.find((item) => normalizeText(item[labelKey]) === text) : undefined;
}

function getFormatoFromCopiedDte(value: unknown, sizeFormat: Array<{ id: number; name: string; size: number }>): { id: number; name: string; size: number } | undefined {
	const formato = Number(value);
	if (isNaN(formato) || formato <= 0) return undefined;

	return sizeFormat.find((item) => Number(item.size) === formato) ?? sizeFormat.find((item) => Number(item.id) === formato);
}

function getDiasVencimientoFromCopiedDte(dte: Partial<DteEmitido>): number {
	const diasVencimiento = Number(dte.diasVencimiento);
	if (!isNaN(diasVencimiento) && diasVencimiento > 0) return diasVencimiento;

	const fecha = getDateOrUndefined(dte.fecha);
	const fechaVencimiento = getDateOrUndefined(dte.fechaVencimiento);
	if (!fecha || !fechaVencimiento) return 0;

	const utcFecha = Date.UTC(fecha.getFullYear(), fecha.getMonth(), fecha.getDate());
	const utcVencimiento = Date.UTC(fechaVencimiento.getFullYear(), fechaVencimiento.getMonth(), fechaVencimiento.getDate());
	return Math.max(Math.round((utcVencimiento - utcFecha) / 86400000), 0);
}

function getDateOrUndefined(value: unknown): Date | undefined {
	if (!value) return undefined;
	const date = value instanceof Date ? value : new Date(value as string);
	return isNaN(date.getTime()) ? undefined : date;
}

function normalizeText(value: unknown): string {
	return String(value ?? "")
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.trim()
		.toLowerCase();
}

export function buildOptionsReferenceForCopiedDte(dte: Partial<DteEmitido>, referencias: ReferenciaDteEmitido[]): OptionsReferenceDteEmitido {
	const options = new OptionsReferenceDteEmitido();
	options.haveDteReference = referencias.length > 0;
	options.listaReferencias = referencias.map((referencia) => Object.assign(new ReferenciaDteEmitido(), referencia));
	options.tipoMoneda = dte.tipoMoneda ?? "CLP";
	options.sonDetallesConIVA = dte.indicadorMontosNetos != null ? !dte.indicadorMontosNetos : false;
	options.tipoMonedaAduana = dte.tipoMonedaAduana;
	options.codigoMonedaAduana = dte.codigoMonedaAduana;
	options.dtePagado = dte.dtePagado ?? false;

	return options;
}
